"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sparkles, Droplets, Bird, Lightbulb } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LEtoilePage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-blue-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-blue-500">/</span>
            <span className="text-sm">L'Étoile</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 rounded-xl p-8 mb-8 border border-blue-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-l-etoile.svg"
                  alt="L'Étoile Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-blue-900/40 to-cyan-900/40 border border-blue-500/30 flex-col items-center justify-center text-blue-300">
                  <span className="text-3xl font-bold mb-2">XVII</span>
                  <span className="text-lg font-semibold">L'ÉTOILE</span>
                  <span className="text-sm text-center mt-2">The Star</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XVII</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">L'Étoile</h1>
                  <p className="text-xl text-blue-400">Umut / İlham ve Manevi Rehberlik</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde on yedinci numara. Umut, ilham ve manevi yenilenmeyi temsil eden
                L'Étoile, geleceğe dair iyimserlik ve içsel rehberlik anlamına gelir. Bu kart,
                ruhsal aydınlanma ve evrensel bağlantı simgesi olarak kararlarda yol gösterir.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Umut</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">İlham</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Rehberlik</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Yenilenme</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Star className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Umut ve İyimserlik</h3>
                  <p className="text-slate-400 text-sm">Geleceğe dair olumlu beklentiler, umut dolu yaklaşım ve iyimserlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Manevi Rehberlik</h3>
                  <p className="text-slate-400 text-sm">İçsel rehberlik, sezgi gücü ve ruhsal yönlendirme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İlham ve Yaratıcılık</h3>
                  <p className="text-slate-400 text-sm">Sanatsal ilham, yaratıcı enerji ve yeni fikirler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yenilenme ve İyileşme</h3>
                  <p className="text-slate-400 text-sm">Ruhsal yenilenme, iyileşme süreci ve iç huzur bulma.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Umut Kaybı</h3>
                  <p className="text-slate-400 text-sm">Pesimizm, çaresizlik ve geleceğe dair endişeler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Manevi Bağlantıyı Kaybetme</h3>
                  <p className="text-slate-400 text-sm">Sezgileri dinlememe, iç sesini susturma ve ruhsal kopukluk.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yaratıcı Engel</h3>
                  <p className="text-slate-400 text-sm">İlham yoksunluğu, yaratıcılık eksikliği ve sanatsal blokaj.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Geçmişe Takılma</h3>
                  <p className="text-slate-400 text-sm">Eski yaralar, iyileşmenin tamamlanmaması ve iç huzursuzluk.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Star className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Büyük Yıldız</h3>
              <p className="text-slate-400 text-sm">Manevi rehberlik, umut ve ilahi ışık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yedi Küçük Yıldız</h3>
              <p className="text-slate-400 text-sm">Çakralar, enerji merkezleri ve ruhsal denge.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👤</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Çıplak Kadın</h3>
              <p className="text-slate-400 text-sm">Saflık, doğallık ve ruhsal berraklık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Droplets className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Su Testi</h3>
              <p className="text-slate-400 text-sm">Duygusal akış, temizlik ve yenilenme.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌿</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Doğal Peyzaj</h3>
              <p className="text-slate-400 text-sm">Uyum, huzur ve doğayla bağlantı.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Bird className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kuş</h3>
              <p className="text-slate-400 text-sm">Ruhsal özgürlük, yüksek bilinç ve mesaj.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhsal bağlantı</li>
                <li>• İyileşme ve bağışlanma</li>
                <li>• Yeni başlangıç umutları</li>
                <li>• Platonik aşk</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yaratıcı projeler</li>
                <li>• İlham verici liderlik</li>
                <li>• Sanatta başarı</li>
                <li>• Yardım etme meslekleri</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-blue-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Sezgi geliştirme</li>
                <li>• İç huzur bulma</li>
                <li>• Özgüven yenileme</li>
                <li>• Ruhsal olgunlaşma</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-cyan-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhsal aydınlanma</li>
                <li>• Evrensel bağlantı</li>
                <li>• Meditatif huzur</li>
                <li>• İlahi rehberlik</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-maison-dieu"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: La Maison Dieu
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-lune"
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Sonraki: La Lune
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}